import {TFunction} from 'i18next';
import {DataSetReportStatusItem} from '../../../common/aur-pizza-traker-progress/AurPizzaTrackerProgress';

export interface EvidenceProgressResponseType {
  evidenceSource: string;
  clientReferenceNumber: string;
  custodianType: string;
  uploadCompletion: string;
  dedupeMethod: string;
  processingOnCompletion: string;
  safeId: number;
  evidMediaId: number;
  isCompleted: boolean;
  sessionStartedOn: string;
  sessionCompletedOn: string;
  totalSpan: string;
  statuses: IntakeProgressStepStatusType[];
  uponUploadCompletion?: string;
}

export interface IntakeProgressStepStatusType {
  evidMediaId: number;
  eventType: IntakeProgressEventType;
  startedOn: string;
  completedOn: string;
  hasError: boolean;
  passNumber: number;
  totalPasses: number;
  isCompleted: boolean;
}

export type IntakeProgressEventType =
  | 'DataCopy'
  | 'VirusScan'
  | 'CopyToOutput'
  | 'Parse'
  | 'Batch'
  | 'Extract';

export interface ProgressStepItemType extends DataSetReportStatusItem {
  status: IntakeProgressEventType;
}

export const getInitialIntakeProgressSteps = (
  t: TFunction<['translation', ...string[]], undefined>
): ProgressStepItemType[] => {
  return [
    {
      status: 'DataCopy',
      tooltipInfo: t('evidenceSource.tooltip.dataCopying'),
      stepProcessingStatus: 'NotStarted',
      statusLabel: t('evidenceSource.intakeStatus.dataCopy'),
      errorLabel: '',
    },
    {
      status: 'Extract',
      tooltipInfo: t('evidenceSource.tooltip.intakeExtraction'),
      stepProcessingStatus: 'NotStarted',
      statusLabel: t('evidenceSource.intakeStatus.extraction'),
      errorLabel: '',
    },
    {
      status: 'VirusScan',
      tooltipInfo: t('evidenceSource.tooltip.intakeVirusScanning'),
      stepProcessingStatus: 'NotStarted',
      statusLabel: t('evidenceSource.intakeStatus.virusScanning'),
      errorLabel: '',
    },
    {
      status: 'CopyToOutput',
      tooltipInfo: t('evidenceSource.tooltip.intakeCopyToOutput'),
      stepProcessingStatus: 'NotStarted',
      statusLabel: t('evidenceSource.intakeStatus.copyOutput'),
      errorLabel: '',
    },
    {
      status: 'Parse',
      tooltipInfo: t('evidenceSource.tooltip.intakeBeingParsed'),
      stepProcessingStatus: 'NotStarted',
      statusLabel: t('evidenceSource.intakeStatus.parsed'),
      errorLabel: '',
    },
    {
      status: 'Batch',
      tooltipInfo: t('evidenceSource.tooltip.intakeBatching'),
      stepProcessingStatus: 'NotStarted',
      statusLabel: t('evidenceSource.intakeStatus.batching'),
      errorLabel: '',
    },
  ];
};

export const getUpdatedIntakeProgressSteps = (
  steps: ProgressStepItemType[],
  response: EvidenceProgressResponseType
) => {
  const statuses = response.statuses ?? [];

  const statusMap = new Map(statuses.map((s) => [s.eventType, s]));

  const stepOrder = steps.map((s) => s.status);

  const furthestIndex = Math.max(
    ...statuses.map((s) => stepOrder.indexOf(s.eventType)),
    -1
  );

  // 🔥 Check if Batch is completed
  const isBatchCompleted = statusMap.get('Batch')?.isCompleted;

  let currentStep: IntakeProgressEventType = 'DataCopy';

  const updatedStep = steps.map((item, index) => {
    const existInUpdate = statusMap.get(item.status);
    const updateItem = {...item};

    // ✅ Case 3: Batch completed → everything completed
    if (isBatchCompleted) {
      updateItem.stepProcessingStatus = 'Completed';
      currentStep = 'Batch';
      if (existInUpdate) {
        // exist in status so update its completed date
        updateItem.completeDate = existInUpdate.completedOn || undefined;
      }
      return updateItem;
    }

    if (existInUpdate) {
      if (existInUpdate.isCompleted) {
        updateItem.stepProcessingStatus = 'Completed';
        updateItem.completeDate = existInUpdate.completedOn || undefined;

        // fallback current step = last completed
        currentStep = existInUpdate.eventType;
      } else if (existInUpdate.hasError) {
        updateItem.stepProcessingStatus = 'Failed';
        updateItem.failedDate = existInUpdate.completedOn || undefined;

        // ✅ Failed becomes current step
        currentStep = existInUpdate.eventType;
      } else if (existInUpdate.startedOn) {
        updateItem.stepProcessingStatus = 'InProgress';
        updateItem.startDate = existInUpdate.startedOn || undefined;

        // ✅ Active step
        currentStep = existInUpdate.eventType;
      }
    } else {
      // ✅ Case 4: Missing step handling
      if (index < furthestIndex) {
        updateItem.stepProcessingStatus = 'Completed';
      } else {
        updateItem.stepProcessingStatus = 'NotStarted';
      }
    }

    return updateItem;
  });

  return {updatedStep, currentStep};
};
